/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Sunday, 24th March 2019
 *
 *  High Order Component (HOC) for Google Maps API Services
 *
 *  Notes:  In order to use this API, please add the below to your index.html header closing tag.
 *  <script src='https://maps.googleapis.com/maps/api/js?key=%REACT_APP_GOOGLE_PLACES_API_KEY%&libraries=places'></script>
 *
 *  Replace %REACT_APP_GOOGLE_PLACES_API_KEY% with your API key or create a .env file with this variable.
 */
import React from 'react';
import GoogleMaps from 'api/googleMaps';

const withGoogleMaps = WrappedComponent => {
  return class extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        isApiReady: false,
        isApiFetching: false,
        isApiDisabled: false,
        isApiAbuse: false,
        mapsApiRequestCount: 0,
        maxRequestsAllowedPerInstance: 100,
      };
      this.init = this.init.bind(this);
      this.getPlaceBusinessDetails = this.getPlaceBusinessDetails.bind(this);
      this.getPlaceAddressComponents = this.getPlaceAddressComponents.bind(this);
      this.getPlacePhoto = this.getPlacePhoto.bind(this);
      this.getPlaceDetails = this.getPlaceDetails.bind(this);
      this.getAutoCompleteResults = this.getAutoCompleteResults.bind(this);
      this.geocodeByPlaceId = this.geocodeByPlaceId.bind(this);
      this.geocodeByAddress = this.geocodeByAddress.bind(this);
      this.enableGeoLocate = this.enableGeoLocate.bind(this);
      this.getLatLng = this.getLatLng.bind(this);
      this.isValidLatLng = this.isValidLatLng.bind(this);
    }

    // Triggered on each API request to Google Maps API
    onRequest = () => {
      var isRequesting = true;

      // Update State
      this.setState({
        mapsApiRequestCount: (this.mapsApiRequestCount += 1),
        mapsApiStatus: { isApiFetching: true },
      });

      // Check for request abuse
      if (this.state.mapsApiRequestCount >= this.state.maxRequestsAllowedPerInstance) {
        this.setState({
          mapsApiStatus: {
            isApiDisabled: true,
            isApiAbuse: true,
          },
        });
        // TODO: Add notification toast here for a response to the user.
        // TODO: Log Abuse request to event handler.

        // No request made.
        isRequesting = false;

        // Update State
        this.setState({
          mapsApiStatus: {
            isApiFetching: false,
          },
        });
      }

      return isRequesting;
    };

    onResponse = () => {
      this.setState({
        mapsApiStatus: {
          isApiFetching: false,
        },
      });
    };

    /**
     * Helper that checks whether an array consists of objects
     * with lat and lng properties
     * @param {object} elem the element to check
     * @returns {boolean} whether or not it's valid
     */
    isValidLatLng = elem => {
      const response =
        elem !== null && typeof elem === 'object' && elem.hasOwnProperty('lat') && elem.hasOwnProperty('lng');

      return response;
    };

    /**
     * Helper that checks whether an array consists of objects
     * with lat and lng properties
     * @param {object} result or Promise containing the results to parse
     * @returns {Promise} containing latLng Object lat & lng parsed from geocode request object
     */
    getLatLng = (result, cb) => {
      return new Promise((resolve, reject) => {
        try {
          const latLng = {
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng(),
          };
          resolve(latLng);
        } catch (e) {
          reject(e);
        }
      });
    };

    // Bias the autocomplete service to the users location if available.
    enableGeoLocate = biasResults => {
      // If Bias Results enabled and device supports geolocation
      if (biasResults && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(position => {
          const geolocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          const circle = new this.mapsApi.maps.Circle({
            center: geolocation,
            radius: position.coords.accuracy,
          });
          this.mapsApi.autocomplete.setBounds(circle.getBounds());
        });
      }
    };

    // Return Geocode Results for a given address string.
    geocodeByAddress = address => {
      this.onRequest();
      const OK = this.mapsApi.GeocoderStatus.OK;
      return new Promise((resolve, reject) => {
        this.mapsApi.geocoder.geocode({ address }, (results, status) => {
          this.onResponse();
          if (status !== OK) {
            reject(status);
          }
          resolve(results);
        });
      });
    };

    // Return Geocode Results for a given Place Id string.
    geocodeByPlaceId = placeId => {
      this.onRequest();
      const OK = this.mapsApi.GeocoderStatus.OK;

      return new Promise((resolve, reject) => {
        this.mapsApi.geocoder.geocode({ placeId }, (results, status) => {
          this.onResponse();
          if (status !== OK) {
            reject(status);
          }
          resolve(results);
        });
      });
    };

    // Return Places Autocomplete results for a given value or requestProps
    getAutoCompleteResults(requestProps) {
      this.onRequest();
      const OK = this.mapsApi.places.PlacesServiceStatus.OK;

      return new Promise((resolve, reject) => {
        this.mapsApi.autocompleteService.getPlacePredictions(requestProps, (results, status) => {
          this.onResponse();
          if (status !== OK) {
            reject(status);
          }
          resolve(results);
        });
      });
    }

    // Returns PlacesService Place Details (More fields than base service returns)
    getPlaceDetails = detailProps => {
      this.onRequest();
      const OK = this.mapsApi.places.PlacesServiceStatus.OK;

      return new Promise((resolve, reject) => {
        this.mapsApi.placesService.getDetails(detailProps, (results, status) => {
          this.onResponse();
          if (status !== OK) {
            reject(status);
          }
          resolve(results);
        });
      });
    };

    // Returns Photo tied to Google PlaceId
    getPlacePhoto = photoProps => {
      this.onRequest();
      const OK = this.mapsApi.places.PlacesServiceStatus.OK;

      return new Promise((resolve, reject) => {
        this.mapsApi.placesService.placesPhoto(photoProps, (results, status) => {
          this.onResponse();
          if (status !== OK) {
            reject(status);
          }
          resolve(results);
        });
      });
    };

    // Returns address_components response fields tied to Google PlaceId
    getPlaceAddressComponents = detailProps => {
      this.onRequest();
      const OK = this.mapsApi.places.PlacesServiceStatus.OK;

      // Check for placeId
      if (!detailProps.placeId) {
        throw new Error('[GoogleMaps]: detailProps must include placeId value.');
      }

      // Check detailProps for types and replace with address_components
      if (detailProps.fields !== ['address_components']) {
        detailProps.fields = ['address_components'];
      }

      return new Promise((resolve, reject) => {
        this.mapsApi.placesService.getDetails(detailProps, (results, status) => {
          this.onResponse();
          if (status !== OK) {
            reject(status);
          }
          resolve(results);
        });
      });
    };

    // Returns address_components, formatted_address, name, formatted_phone_number, website, response fields tied to Google PlaceId
    getPlaceBusinessDetails = detailProps => {
      this.onRequest();
      const OK = this.mapsApi.places.PlacesServiceStatus.OK;

      // Check for placeId
      if (!detailProps.placeId) {
        throw new Error('[GoogleMaps]: detailProps must include placeId value.');
      }

      // Update detailProps with correct field types
      detailProps.fields = ['name', 'address_components', 'formatted_phone_number', 'website', 'type', 'utc_offset_minutes'];

      return new Promise((resolve, reject) => {
        this.mapsApi.placesService.getDetails(detailProps, (results, status) => {
          this.onResponse();
          if (status !== OK) {
            reject(status);
          }
          resolve(results);
        });
      });
    };

    // Wait to initialize the Google Map API Services until we validate the component loaded successfully.
    init = () => {
      // Check to make sure Google Maps API Library is loaded.
      if (!window.google) {
        // Error Message
        const msg = '[GoogleMaps]:[withGoogleMaps]: Google Maps API library must be loaded.';

        // Update Error State
        throw new Error(msg);
      }

      // Check to make sure Google Maps Places library is loaded.
      if (!window.google.maps.places) {
        // Error Message
        const msg = '[GoogleMaps]:[withGoogleMaps]: Google Maps Places library must be loaded.';

        // Update Error State
        throw new Error(msg);
      }

      // Init new GoogleMaps Object
      this.mapsApi = new GoogleMaps();

      if (this.state.isApiReady) {
        return;
      } else {
        this.setState({ isApiReady: true });
      }
    };

    render() {
      const { ...otherProps } = this.props;
      return (
        <WrappedComponent
          init={this.init}
          mapsApi={this.mapsApi}
          isApiReady={this.state.isApiReady}
          isApiFetching={this.state.isApiFetching}
          isApiDisabled={this.state.isApiDisabled}
          isApiAbuse={this.state.isApiAbuse}
          apiRequestCount={this.state.apiRequestCount}
          // Passthrough Methods
          getPlaceBusinessDetails={this.getPlaceBusinessDetails}
          getPlaceAddressComponents={this.getPlaceAddressComponents}
          getPlacePhoto={this.getPlacePhoto}
          getPlaceDetails={this.getPlaceDetails}
          getAutoCompleteResults={this.getAutoCompleteResults}
          geocodeByPlaceId={this.geocodeByPlaceId}
          geocodeByAddress={this.geocodeByAddress}
          enableGeoLocate={this.enableGeoLocate}
          getLatLng={this.getLatLng}
          isValidLatLng={this.isValidLatLng}
          // Pass through all parent props
          {...otherProps}
        />
      );
    }
  };
};
export default withGoogleMaps;
